import _ from 'underscore';

import {AaSequenceAlignmentRow} from 'constants/AlignmentConstants';
import {aminoAcidMatches} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentUtil';

export function getAminoAcidMatrix(
  alignmentRows: Array<AaSequenceAlignmentRow>,
  selectionStart: number,
  selectionEnd: number | null
): Array<Array<string>> {
  // Take the entire sequence if selection does not have an end
  const effectiveStart = selectionEnd ? selectionStart : 0;
  const effectiveEnd = selectionEnd || undefined;
  return alignmentRows.map((row) =>
    Array.from(row.proteinVersion.aminoAcids.slice(effectiveStart, effectiveEnd))
  );
}

export function getLength(aminoAcidMatrix: Array<Array<string>>): number {
  return aminoAcidMatrix[0].length;
}

export function getNumAaSequences(aminoAcidMatrix: Array<Array<string>>): number {
  return aminoAcidMatrix.length;
}

export function getIdenticalSites(aminoAcidMatrix: Array<Array<string>>): number {
  return aminoAcidMatrix[0].filter((referenceAminoAcid, index) => {
    const aminoAcidsAtIndex = aminoAcidMatrix.map((row) => row[index]);
    return aminoAcidsAtIndex.every((aminoAcid) => aminoAcidMatches(aminoAcid, referenceAminoAcid));
  }).length;
}

function getSum(arr: Array<number>): number {
  return arr.reduce((accumulator, value) => accumulator + value, 0);
}

function getNumPairs(n: number): number {
  return (n * (n - 1)) / 2;
}

function getNumMatchingPairsOfList(aminoAcidList: Array<string>): number {
  const normalizedAminoAcidList = aminoAcidList.map((aminoAcid) => aminoAcid.toUpperCase());
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const aminoAcidCounts = _.countBy(normalizedAminoAcidList, _.identity);
  // eslint-disable-next-line no-restricted-syntax
  const nonGapAminoAcidCounts = _.omit(aminoAcidCounts, '-');
  // eslint-disable-next-line no-restricted-syntax
  return getSum(_.values(nonGapAminoAcidCounts).map((count) => getNumPairs(count)));
}

/**
 * Return an array of the number of matching pairs of amino acids at each index.
 */
export function getNumMatchingPairs(aminoAcidMatrix: Array<Array<string>>): Array<number> {
  return _.zip(...aminoAcidMatrix).map((list) => getNumMatchingPairsOfList(list));
}

function getNumNonGapPairsOfList(aminoAcidList: Array<string>): number {
  const numGaps = aminoAcidList.filter((aminoAcid) => aminoAcid === '-').length;
  return getNumPairs(aminoAcidList.length) - getNumPairs(numGaps);
}

/**
 * Return an array of the number of pairs of amino acids, where one but not both can be a gap, at each index.
 */
export function getNumNonGapPairs(aminoAcidMatrix: Array<Array<string>>): Array<number> {
  return _.zip(...aminoAcidMatrix).map((list) => getNumNonGapPairsOfList(list));
}

/**
 * Return the pairwise percent identity, which is defined as the total number of matching pairs over
 * the total number of non-gap pairs.
 */
export function getPairwisePercentIdentity(
  numMatchingPairs: Array<number>,
  numNonGapPairs: Array<number>
): number {
  return (100 * getSum(numMatchingPairs)) / getSum(numNonGapPairs);
}
